/**
 * Certificates — server state.
 *
 * The one thing to know here: issuing a certificate changes the figures the
 * table is showing. `last_issued_at` and `last_certificate_minutes` come from
 * the same rows, so a successful issue invalidates the summaries — otherwise the
 * button stays offering to do a thing that has just been done.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Certificate, CertificateActivity, CertificateIssueResult } from '@parinaam-vms/shared';
import { useToast } from '../components/common';
import {
  fetchCertificatePdf,
  fetchCertificateSummaries,
  fetchCertificates,
  fetchVolunteerActivities,
  issueCustomCertificate,
} from '../services/api/certificates';
import { issueCertificates } from '../services/api/certificates';
import type { CertificateSummaryQuery } from '../services/api/certificates';
import { certificateKeys } from './queryKeys';

export { certificateKeys };

/**
 * The admin table.
 *
 * `enabled` is the caller's business, as with the demo-data status: only an
 * ADMIN may ask, and asking as a TEAM account answers 403 — which would put an
 * error on a screen that should simply not offer the feature.
 */
export function useCertificateSummaries(
  params: CertificateSummaryQuery,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: certificateKeys.summaries(params),
    queryFn: () => fetchCertificateSummaries(params),
    enabled: options?.enabled ?? true,
  });
}

/**
 * One volunteer's activities, for an expanded row.
 *
 * Loaded per row and only when opened, following `AttendanceTable`'s history
 * panel: a table of twenty rows firing twenty breakdown queries on render is
 * not a table, it is a load test.
 */
export function useVolunteerActivities(volunteerId: string | undefined) {
  return useQuery<CertificateActivity[]>({
    queryKey: certificateKeys.activities(volunteerId ?? ''),
    queryFn: () => fetchVolunteerActivities(volunteerId as string),
    enabled: Boolean(volunteerId),
  });
}

/**
 * Issue to one or many.
 *
 * The toast is deliberately **not** unconditionally cheerful. Partial success is
 * a normal outcome — one volunteer in a selection with hours still awaiting
 * approval — and reporting "certificates issued" over the top of it would hide
 * the one row that needs an admin's attention.
 */
export function useIssueCertificates() {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation<CertificateIssueResult, Error, string[]>({
    mutationFn: (volunteerIds: string[]) => issueCertificates(volunteerIds),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: certificateKeys.all });

      if (result.issued > 0 && result.skipped === 0) {
        toast.success(
          result.issued === 1
            ? 'Certificate issued. It will be emailed if the volunteer has an address on file.'
            : `${result.issued} certificates issued.`
        );
        return;
      }

      if (result.issued > 0) {
        toast.info(
          `${result.issued} issued, ${result.skipped} skipped. The reasons are listed below.`
        );
        return;
      }

      // Nothing was issued, but the request itself succeeded, so this is not an
      // error toast — the per-row reasons on the screen are the answer.
      toast.info(
        result.skipped === 1
          ? 'Nothing issued — see the reason below.'
          : `Nothing issued for any of the ${result.skipped} selected. See the reasons below.`
      );
    },
    onError: (error) => toast.error(error, 'The certificates could not be issued.'),
  });
}

/**
 * Issue a certificate in the admin's own words, to one volunteer.
 *
 * Always either succeeds or throws — there is no `outcomes` array to read the
 * way `useIssueCertificates` has, since a custom certificate is one act for
 * one person rather than a bulk request.
 */
export function useIssueCustomCertificate() {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation<Certificate, Error, { volunteer_id: string; custom_text: string }>({
    mutationFn: ({ volunteer_id, custom_text }) => issueCustomCertificate(volunteer_id, custom_text),
    onSuccess: () => {
      // The issue log the standard tab's "Issued" list and history read from.
      void queryClient.invalidateQueries({ queryKey: certificateKeys.all });
      toast.success('Certificate issued. It will be emailed if the volunteer has an address on file.');
    },
    onError: (error) => toast.error(error, 'The certificate could not be issued.'),
  });
}

/**
 * Download a certificate in the browser.
 *
 * A mutation rather than a query because it is an action with a side effect on
 * the user's machine, and nothing about it should be cached or retried.
 *
 * Two details that look like ceremony and are not:
 *
 * **The anchor goes into the document.** `click()` on a detached `<a download>`
 * is ignored outright by some browsers, and this is the only download in the
 * application — there is no working example elsewhere to copy, so it is spelled
 * out here.
 *
 * **The object URL is revoked on a later task, not in a `finally`.** `click()`
 * only *queues* the download; revoking synchronously can cancel it before it
 * starts, and the failure is invisible — the mutation resolves, so no error
 * appears and no file arrives. It still has to be revoked, because otherwise
 * every download holds its blob for the lifetime of the tab, which on an admin
 * working through a programme's worth of certificates is a real leak.
 */
export function useDownloadCertificate() {
  const toast = useToast();

  return useMutation<void, Error, { certificate_id: string; certificate_code: string }>({
    mutationFn: async ({ certificate_id, certificate_code }) => {
      const blob = await fetchCertificatePdf(certificate_id);
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `Parinaam-Certificate-${certificate_code}.pdf`;
      link.style.display = 'none';
      document.body.append(link);
      link.click();
      link.remove();

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    onError: (error) => toast.error(error, 'The certificate could not be downloaded.'),
  });
}

/**
 * The certificates already issued to one volunteer, newest first.
 *
 * Disabled until a volunteer is chosen: the same endpoint with no
 * `volunteer_id` is the whole issue log, and fetching that by accident behind
 * an empty picker would be a slow, confusing first paint.
 */
export function useVolunteerCertificates(volunteerId: string | undefined, page = 1) {
  const params = { volunteer_id: volunteerId, page, page_size: 20 };

  return useQuery({
    queryKey: certificateKeys.list(params),
    queryFn: () => fetchCertificates({ volunteer_id: volunteerId, page, page_size: 20 }),
    enabled: Boolean(volunteerId),
  });
}
